import { parse, format } from 'date-fns';

export const formatScheduleDisplay = (schedule: any): string => {
  const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  
  const schoolDays = Object.entries(schedule || {})
    .filter(([_, dayData]: [string, any]) => {
      // Only include days that have school AND valid start/end times
      return dayData.hasSchool && 
             dayData.start && 
             dayData.end && 
             dayData.start.trim() !== '' && 
             dayData.end.trim() !== '';
    })
    .map(([dayName, dayData]: [string, any]) => ({
      day: dayName,
      start: dayData.start,
      end: dayData.end,
      halfEnd: dayData.halfEnd,
      supportsHalf: dayData.supportsHalf,
    }))
    .sort((a, b) => dayOrder.indexOf(a.day.toLowerCase()) - dayOrder.indexOf(b.day.toLowerCase()));

  if (schoolDays.length === 0) {
    return "No Schedule Set";
  }

  // Group consecutive days with the same times
  const groups: Array<{
    days: string[];
    start: string;
    end: string;
    halfEnd?: string;
    supportsHalf: boolean;
  }> = [];

  schoolDays.forEach((currentDay) => {
    const lastGroup = groups[groups.length - 1];
    
    if (
      lastGroup && 
      lastGroup.start === currentDay.start && 
      lastGroup.end === currentDay.end &&
      lastGroup.halfEnd === currentDay.halfEnd &&
      lastGroup.supportsHalf === currentDay.supportsHalf
    ) {
      lastGroup.days.push(currentDay.day);
    } else {
      groups.push({
        days: [currentDay.day],
        start: currentDay.start,
        end: currentDay.end,
        halfEnd: currentDay.halfEnd,
        supportsHalf: currentDay.supportsHalf,
      });
    }
  });

  // Format each group
  return groups
    .map((group) => {
      // Format day range
      let dayRange: string;
      if (group.days.length === 1) {
        dayRange = formatDayName(group.days[0]);
      } else if (areConsecutive(group.days)) {
        dayRange = `${formatDayName(group.days[0])}-${formatDayName(group.days[group.days.length - 1])}`;
      } else {
        dayRange = group.days.map(formatDayName).join(", ");
      }

      // Format times to 12-hour
      const formatTime = (time: string | null | undefined) => {
        // Handle null, undefined, or empty strings
        if (!time || (typeof time === 'string' && time.trim() === '')) {
          return 'Invalid Time';
        }
        
        try {
          const parsed = parse(time, 'HH:mm', new Date());
          return format(parsed, 'h:mm a');
        } catch (error) {
          return 'Invalid Time';
        }
      };

      const startTime = formatTime(group.start);
      const endTime = formatTime(group.end);
      
      let result = `${dayRange}: ${startTime} - ${endTime}`;
      
      // Add half-day info if supported
      if (group.supportsHalf && group.halfEnd) {
        const halfEndTime = formatTime(group.halfEnd);
        result += ` (${halfEndTime} Half Day)`;
      }
      
      return result;
    })
    .join(", ");
};

const formatDayName = (day: string): string => {
  const dayMap: Record<string, string> = {
    monday: "Mon",
    tuesday: "Tue", 
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
  };
  return dayMap[day.toLowerCase()] || day.charAt(0).toUpperCase() + day.slice(1, 3);
};

const areConsecutive = (days: string[]): boolean => {
  const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const indices = days.map(day => dayOrder.indexOf(day.toLowerCase())).sort((a, b) => a - b);
  
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] !== indices[i - 1] + 1) {
      return false;
    }
  }
  return true;
};

// Helper to get enrollment type display with times
export const getEnrollmentTypeDisplay = (
  enrollmentType: 'full' | 'half',
  schedule: any
): string => {
  const firstSchoolDay = Object.values(schedule || {}).find((day: any) => day.hasSchool) as any;
  
  if (!firstSchoolDay) {
    return enrollmentType === 'full' ? 'Full Day' : 'Half Day';
  }

  const formatTime = (time: string) => {
    const parsed = parse(time, 'HH:mm', new Date());
    return format(parsed, 'h:mm a');
  };

  const startTime = formatTime(firstSchoolDay.start);
  
  if (enrollmentType === 'full') {
    const endTime = formatTime(firstSchoolDay.end);
    return `🕐 Full Day (${startTime} - ${endTime})`;
  } else {
    const halfEndTime = firstSchoolDay.halfEnd ? formatTime(firstSchoolDay.halfEnd) : '12:30 PM';
    return `⏰ Half Day (${startTime} - ${halfEndTime})`;
  }
};